/* SpecIntroVideo — one-time intro screen.
   Originally built for Inner Specializations, now parameterised so the exact
   same layout/behaviour can be reused by other paths through props. The
   Inner Specializations entry point still calls it with zero props and gets
   the identical video, eyebrow, and seen-key as before — no visible change. */
import React, { useState } from 'react';
import { Play, ArrowRight, ExternalLink } from 'lucide-react';
import Navbar from './Navbar';

// Legacy defaults — DO NOT change: Inner Specializations depends on these.
const SPEC_VIDEO_ID   = 'Mcqlie4Iupk';
const SPEC_EYEBROW    = 'Inner Specializations';
const SPEC_SEEN_KEY   = (user) => `spec_intro_seen_${user?.user_id || 'anon'}`;

// Generic per-path seen key used by every OTHER path (Career Clarity, AI Pathway,
// Banking, Blue-Collar, Build Badges, Is AI Coming for Your Role, Future-Ready
// Job Roles, Entrepreneurship · OwnPath). Each path key has its own state so
// seeing one intro never marks another as seen.
const pathSeenKey = (pathKey, user) => `intro_seen_${pathKey}_${user?.user_id || 'anon'}`;

// ── Legacy exports (Inner Specializations — untouched) ──
export const specIntroSeen = (user) => {
  return true;
};
export const markSpecIntroSeen = (user) => {
  try { localStorage.setItem(SPEC_SEEN_KEY(user), '1'); } catch { /* ignore */ }
};

// ── Generic per-path exports (the 8 new paths use these) ──
export const introSeen = (pathKey, user) => {
  return true;
};
export const markIntroSeen = (pathKey, user) => {
  try { localStorage.setItem(pathSeenKey(pathKey, user), '1'); } catch { /* ignore */ }
};

const isInAppBrowser = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  return /instagram|fban|fbav|whatsapp|line\/|micromessenger/.test(ua);
};

export default function SpecIntroVideo({
  onDone,
  videoId = SPEC_VIDEO_ID,
  eyebrow = SPEC_EYEBROW,
}) {
  const [playing, setPlaying] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);
  const inApp = isInAppBrowser();

  const WATCH_URL = `https://www.youtube.com/watch?v=${videoId}`;
  const EMBED_URL = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
  const THUMB_URL = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div className="h-[100dvh] min-h-[560px] overflow-y-auto bg-gradient-to-br from-teal-50 via-white to-blue-50 flex flex-col" data-testid="spec-intro-screen">
      <Navbar />
      <div className="flex-1 min-h-0 flex flex-col items-center px-4 pt-[104px] md:pt-28 pb-24 sm:pb-20">
        <p className="text-[11px] font-black uppercase tracking-[0.25em] text-teal-700 mb-1">{eyebrow}</p>
        <h1 className="text-lg md:text-2xl font-black text-gray-900 mb-3 text-center">A 60-second intro before you begin</h1>

        {/* Phone-shaped 9:16 frame — flexes to available height, buttons always on screen */}
        <div className="flex-1 min-h-0 w-full flex justify-center">
          <div
            className="relative rounded-[28px] overflow-hidden bg-gray-900 shadow-2xl border-4 border-gray-900"
            style={{ aspectRatio: '9 / 16', height: '100%', maxHeight: 640, maxWidth: '100%' }}
            data-testid="spec-intro-video-frame"
          >
          {playing && !inApp ? (
            <iframe
              src={EMBED_URL}
              title={`${eyebrow} intro`}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              loading="lazy"
              data-testid="spec-intro-iframe"
            />
          ) : (
            <button
              type="button"
              onClick={() => (inApp ? window.open(WATCH_URL, '_blank') : setPlaying(true))}
              className="absolute inset-0 w-full h-full group"
              data-testid="spec-intro-play-btn"
              aria-label="Play intro video"
            >
              {!thumbBroken ? (
                <img
                  src={THUMB_URL}
                  alt=""
                  loading="lazy"
                  onError={() => setThumbBroken(true)}
                  className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-teal-800 to-gray-900" />
              )}
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <span className="w-16 h-16 rounded-full bg-white/95 shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={26} className="text-teal-700 ml-1" fill="currentColor" />
                </span>
                {inApp && (
                  <span className="inline-flex items-center gap-1.5 text-white text-xs font-bold bg-black/50 px-3 py-1.5 rounded-full">
                    ▶ Watch intro on YouTube <ExternalLink size={11} />
                  </span>
                )}
              </span>
            </button>
          )}
          </div>
        </div>

        {/* Trouble link — user is never stuck on a failed embed */}
        <a
          href={WATCH_URL}
          target="_blank"
          rel="noreferrer"
          className="mt-2 text-[11px] font-semibold text-gray-400 hover:text-gray-600 inline-flex items-center gap-1"
          data-testid="spec-intro-youtube-link"
        >
          Trouble playing? Watch on YouTube <ExternalLink size={10} />
        </a>

        {/* Actions — always visible, never below the fold */}
        <div className="w-full mt-3 flex flex-col items-center gap-1.5 shrink-0" style={{ maxWidth: 360 }}>
          <button
            type="button"
            onClick={onDone}
            className="w-full min-h-[48px] inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-sm rounded-full shadow-lg hover:shadow-xl transition-all"
            data-testid="spec-intro-continue-btn"
          >
            Continue to previews <ArrowRight size={16} />
          </button>
          <button
            type="button"
            onClick={onDone}
            className="w-full min-h-[44px] inline-flex items-center justify-center gap-1.5 border-2 border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 font-bold text-sm rounded-full transition-colors"
            data-testid="spec-intro-skip-btn"
          >
            Skip intro →
          </button>
        </div>
      </div>
    </div>
  );
}
